import { useState, useEffect, useRef } from 'react';

interface Participant {
  name: string;
  emoji: string;
}

interface SavedList {
  id: string;
  name: string;
  participants: Participant[];
  createdAt: number;
}

interface NameInputProps {
  onParticipantsChange: (participants: Participant[]) => void;
}

const STORAGE_KEY = 'pint-roulette-saved-lists';

export default function NameInput({ onParticipantsChange }: NameInputProps) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [animatingItems, setAnimatingItems] = useState<Set<number>>(new Set());
  const [removingItems, setRemovingItems] = useState<Set<number>>(new Set());
  const [savedLists, setSavedLists] = useState<SavedList[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadDropdown, setShowLoadDropdown] = useState(false);
  const [saveListName, setSaveListName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const loadDropdownRef = useRef<HTMLDivElement>(null);

  const colors = [
    '#FF6B6B', '#4ECDC4', '#FFD93D', '#6BCF7F', '#A78BFA',
    '#FF8C42', '#2EC4B6', '#FF5E78', '#FFC857', '#7B68EE',
    '#FF6F91', '#00D9FF', '#FFB627', '#5FD068', '#C77DFF'
  ];

  const emojiOptions = [
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
    '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🦄',
    '🐝', '🐛', '🦋', '🐌', '🐞', '🐢', '🐙', '🦀', '🐠', '🐡',
    '😀', '😎', '🤩', '🥳', '😇', '🤠', '👻', '🤖', '👽', '🎃',
    '🦸', '🧙', '🧚', '🧜', '🦹', '🥷', '👸', '🤴', '🧛', '🧟'
  ];

  // Load saved lists from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setSavedLists(JSON.parse(stored));
      } catch {
        setSavedLists([]);
      }
    }
  }, []);

  useEffect(() => {
    onParticipantsChange(participants);
  }, [participants, onParticipantsChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (loadDropdownRef.current && !loadDropdownRef.current.contains(event.target as Node)) {
        setShowLoadDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddName = () => {
    const trimmed = inputValue.trim();
    if (trimmed !== '') {
      const newIndex = participants.length;
      const randomEmoji = emojiOptions[Math.floor(Math.random() * emojiOptions.length)];
      
      setParticipants(prev => [...prev, { name: trimmed, emoji: randomEmoji }]);
      setInputValue('');
      
      setAnimatingItems(prev => new Set(prev).add(newIndex));
      setTimeout(() => {
        setAnimatingItems(prev => {
          const next = new Set(prev);
          next.delete(newIndex);
          return next;
        });
      }, 300);
    }
  };

  const handleRemoveName = (index: number) => {
    setRemovingItems(prev => new Set(prev).add(index));
    
    setTimeout(() => {
      setParticipants(prev => prev.filter((_, i) => i !== index));
      setRemovingItems(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }, 250);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddName();
    }
  };

  const handleClearAll = () => {
    const allIndices = new Set(participants.map((_, i) => i));
    setRemovingItems(allIndices);
    
    setTimeout(() => {
      setParticipants([]);
      setRemovingItems(new Set());
      setInputValue('');
    }, 250);
  };

  const handleSaveList = () => {
    if (saveListName.trim() === '' || participants.length === 0) return;
    
    const newList: SavedList = {
      id: Date.now().toString(),
      name: saveListName.trim(),
      participants: [...participants],
      createdAt: Date.now()
    };
    
    const updatedLists = [newList, ...savedLists];
    setSavedLists(updatedLists);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedLists));
    
    setSaveListName('');
    setShowSaveModal(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleLoadList = (list: SavedList) => {
    // Animate current items out first
    if (participants.length > 0) {
      const allIndices = new Set(participants.map((_, i) => i));
      setRemovingItems(allIndices);
      
      setTimeout(() => {
        setParticipants([...list.participants]);
        setRemovingItems(new Set());
        setShowLoadDropdown(false);
        
        // Animate new items in
        list.participants.forEach((_, index) => {
          setTimeout(() => {
            setAnimatingItems(prev => new Set(prev).add(index));
            setTimeout(() => {
              setAnimatingItems(prev => {
                const next = new Set(prev);
                next.delete(index);
                return next;
              });
            }, 300);
          }, index * 50);
        });
      }, 250);
    } else {
      setParticipants([...list.participants]);
      setShowLoadDropdown(false);
      
      list.participants.forEach((_, index) => {
        setTimeout(() => {
          setAnimatingItems(prev => new Set(prev).add(index));
          setTimeout(() => {
            setAnimatingItems(prev => {
              const next = new Set(prev);
              next.delete(index);
              return next;
            });
          }, 300);
        }, index * 50);
      });
    }
  };

  const handleDeleteSavedList = (e: React.MouseEvent, listId: string) => {
    e.stopPropagation();
    const updatedLists = savedLists.filter(l => l.id !== listId);
    setSavedLists(updatedLists);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedLists));
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-gray-200 p-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h2 className="text-base font-bold text-gray-900">
          Add Participants
        </h2>
        <div className="flex items-center gap-2">
          {participants.length > 0 && (
            <>
              <button
                onClick={() => setShowSaveModal(true)}
                className="text-xs text-[#2349FF] hover:text-[#1a38cc] font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                <i className="ri-save-line"></i>
                Save
              </button>
              <span className="text-gray-200">|</span>
            </>
          )}
          <div className="relative" ref={loadDropdownRef}>
            <button
              onClick={() => setShowLoadDropdown(!showLoadDropdown)}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
            >
              <i className="ri-folder-open-line"></i>
              Load
              <i className={`ri-arrow-down-s-line transition-transform ${showLoadDropdown ? 'rotate-180' : ''}`}></i>
            </button>
            
            {showLoadDropdown && (
              <div className="absolute left-0 sm:right-0 sm:left-auto top-7 z-50 bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-56 animate-fade-in">
                {savedLists.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <i className="ri-folder-line text-lg text-gray-300"></i>
                    </div>
                    <p className="text-xs text-gray-400">No saved lists yet</p>
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto">
                    <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Saved Lists</p>
                    {savedLists.map(list => (
                      <div
                        key={list.id}
                        onClick={() => handleLoadList(list)}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center justify-between group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{list.name}</p>
                          <p className="text-[10px] text-gray-400">
                            {list.participants.length} participants • {new Date(list.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => handleDeleteSavedList(e, list.id)}
                          className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {participants.length > 0 && (
            <>
              <span className="text-gray-200">|</span>
              <button
                onClick={handleClearAll}
                className="text-xs text-red-500 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer"
              >
                Clear All
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save Success Toast */}
      {saveSuccess && (
        <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 animate-fade-in">
          <i className="ri-check-line text-green-500"></i>
          <span className="text-xs text-green-700 font-medium">List saved successfully!</span>
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter a name..."
          className="flex-1 px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2349FF] focus:border-transparent bg-gray-50"
        />
        <button
          onClick={handleAddName}
          disabled={inputValue.trim() === ''}
          className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer ${
            inputValue.trim() === ''
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-[#2349FF] text-white hover:bg-[#1a38cc]'
          }`}
        >
          <i className="ri-add-line mr-1"></i>Add
        </button>
      </div>

      {/* Names List */}
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {participants.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-user-line text-2xl text-gray-300"></i>
            </div>
            <p className="text-gray-400 text-xs">No participants yet. Add some names to get started!</p>
          </div>
        ) : (
          <>
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-400">
                {participants.length} {participants.length === 1 ? 'participant' : 'participants'}
              </span>
            </div>
            {participants.map((participant, index) => {
              const color = colors[index % colors.length];
              const isAnimating = animatingItems.has(index);
              const isRemoving = removingItems.has(index);
              
              return (
                <div
                  key={`${participant.name}-${index}`}
                  className={`flex items-center justify-between p-2.5 rounded-lg group transition-all duration-250 ${
                    isAnimating ? 'animate-slide-in' : ''
                  } ${isRemoving ? 'animate-slide-out' : ''}`}
                  style={{ 
                    backgroundColor: `${color}15`,
                    opacity: isRemoving ? 0 : 1,
                    transform: isRemoving ? 'translateX(-20px) scale(0.95)' : isAnimating ? '' : 'translateX(0) scale(1)'
                  }}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      <span className="text-lg leading-none">{participant.emoji}</span>
                    </div>
                    <span className="font-medium text-sm text-gray-800">{participant.name}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveName(index)}
                    className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                  >
                    <i className="ri-close-line text-base"></i>
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Save Participant List</h3>
            <p className="text-xs text-gray-500 mb-4">Give your list a name to easily find it later</p>
            
            <input
              type="text"
              value={saveListName}
              onChange={(e) => setSaveListName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveList()}
              placeholder="e.g., Friday Night Crew"
              className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2349FF] focus:border-transparent bg-gray-50 mb-4"
              autoFocus
            />
            
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSaveListName('');
                }}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveList}
                disabled={saveListName.trim() === ''}
                className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                  saveListName.trim() === ''
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#2349FF] text-white hover:bg-[#1a38cc]'
                }`}
              >
                <i className="ri-save-line mr-1"></i>Save List
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        
        @keyframes slideOut {
          from {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateX(-20px) scale(0.95);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slide-in {
          animation: slideIn 0.3s ease-out forwards;
        }
        
        .animate-slide-out {
          animation: slideOut 0.25s ease-in forwards;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
